import { Box, useTheme } from '@mui/material'
import _ from 'lodash'
import { useState } from 'react'
import ApiClient from '../../backend/ApiClient'
import { IDataFrame } from '../../definitions/DataAnalytics'
import { ITCOCSummaryFilter } from '../../definitions/Summary'
import Helper from '../../helpers/Helper'
import TimeHelper from '../../helpers/TimeHelper'
import { useAppSelector } from '../../store/Hooks'
import SmartBadgeFilter from '../badge_filter/SmartBadgeFilter'
import { DonutChart, HorizontalBarChart, LineChart } from '../charts'
import { DonutChartDefaultOptions } from '../charts/DonutChart'
import RouteHeader from '../controls/RouteHeader'
import SmartCard from '../controls/cards/SmartCard'
import ChartCard from '../controls/data_visualization/ChartCard'
import KPICard from '../controls/data_visualization/KPICard'
import StaleObserver from '../controls/stale/StaleObserver'
import useQuery, { buildQueryKey } from '../hooks/useQuery'
import Tile from '../layout/Tile'
import ActivityManager from '../../managers/ActivityManager'

export default function TCOCSummary() {
    const time = useAppSelector(s => s.app.timeSettings ?? TimeHelper.timeSettings())
    const theme = useTheme()
    const [filter, setFilter] = useState<ITCOCSummaryFilter>({ contractTypeIds: [], lobIds: [] })
    const [providerPerformanceOrder, setProviderPerformanceOrder] = useState<'worst' | 'best'>('worst')
    const [qmPerformanceOrder, setQMPerformanceOrder] = useState<'below' | 'meets'>('below')

    const { status, response } = useQuery({
        queryKey: buildQueryKey('get-tcoc-summary', TimeHelper.stringify(time), Helper.generateKeyOfObject(filter)),
        queryFn: async () => await ApiClient.getTCOCSummary(time, filter),
        cache: true
    })

    const isLoading = status === 'loading'

    const tcocPerMember: IDataFrame = response?.tcocPerMember
    const revenuePerMember: IDataFrame = response?.revenuePerMember

    const members: IDataFrame = response?.members
    const tcoc: IDataFrame = response?.tcoc
    const totalRevenue: IDataFrame = response?.totalRevenue ?? { points: [{ x: '01/01/2023', y: 20 }, { x: '01/12/2023', y: 100 }], delta: 80, deltaPercentage: 80 }

    const tcocLOB: { name: string, value: number }[] = response?.lobTCOC?.map(x => {
        return { name: x.name, value: _.last(x.dataframe.points).y }
    }) ?? []

    const contractTypesTCOC: { name: string, value: number }[] = response?.contractTypeTCOC?.map(x => {
        return { name: x.name, value: _.last(x.dataframe.points).y }
    }) ?? []

    const providersTCOC: { name: string, value: number }[] = _.orderBy(response?.providersTCOC?.map(x => {
        return { name: x.name, value: _.last(x.dataframe.points).y }
    }) ?? [], 'value').filter(x => x.value !== 0)

    const contractsTCOC: { name: string, value: number }[] = _.orderBy(response?.contractsTCOC?.map(x => {
        return { name: x.name, value: _.last(x.dataframe.points).y }
    }) ?? [], 'value').filter(x => x.value !== 0)

    const orderProvidersTCOC = _.orderBy(providersTCOC, ['value'], providerPerformanceOrder === 'worst' ? 'asc' : 'desc')?.slice(0, 5)
    const orderContractsTCOC = _.orderBy(contractsTCOC, ['value'], qmPerformanceOrder === 'below' ? 'asc' : 'desc')?.slice(0, 5)

    return (
        <Tile.Container>
            <RouteHeader
                primary='Total Cost of Care Summary'
                timeSettings={time}
                subActions={[
                    { label: 'Updated last week', icon: 'clock', onClick: () => ActivityManager.openActivity('contracts') }
                ]}
            />
            <Tile.Row flex={0} spacing={2}>
                <SmartBadgeFilter
                    name='Type'
                    values={filter.contractTypeIds}
                    onApply={x => setFilter(f => ({ ...f, contractTypeIds: x }))}
                />
                <SmartBadgeFilter
                    name='Line Of Business'
                    values={filter.lobIds}
                    onApply={x => setFilter(f => ({ ...f, lobIds: x }))}
                />
            </Tile.Row>
            <Tile.Row flex={0} spacing={2}>
                <SmartCard flex={1}>
                    <KPICard
                        data={Helper.dataframeToKPICard(tcocPerMember)}
                        isLoading={isLoading}
                        unit='$'
                        inverse
                        label='TCOC per Member'
                    />
                </SmartCard>
                <SmartCard flex={1}>
                    <KPICard
                        data={Helper.dataframeToKPICard(revenuePerMember)}
                        isLoading={isLoading}
                        unit='$'
                        label='Revenue per Member'
                    />
                </SmartCard>
                <SmartCard flex={1}>
                    <KPICard
                        data={Helper.dataframeToKPICard(members)}
                        isLoading={isLoading}
                        label='Members'
                    />
                </SmartCard>
            </Tile.Row>
            <Tile.Row flex={0} spacing={2} style={{ minHeight: 400 }}>
                <SmartCard style={{ width: '50%' }}>
                    <ChartCard
                        title='Total Cost of Care vs Total Revenue'
                        chart={(
                            <LineChart
                                isLoading={isLoading}
                                data={{
                                    datasets: [
                                        // { data: tcocClaims?.points?.map(x => x.y), backgroundColor: theme.palette.error[500], borderColor: theme.palette.error[500], label: 'TCOC Claims' },
                                        // { data: tcocPrograms?.points?.map(x => x.y), backgroundColor: theme.palette.error[600], borderColor: theme.palette.error[600], label: 'TCOC Programs' },
                                        { data: tcoc?.points?.map(x => x.y), backgroundColor: theme.palette.error[700], borderColor: theme.palette.error[700], label: 'TCOC', unit: '$' },
                                        { data: totalRevenue?.points?.map(x => x.y), label: 'Total Revenue', unit: '$' }
                                    ],
                                    labels: tcoc?.points?.map(x => x.x)
                                }}
                            />
                        )}
                    />
                </SmartCard>
                <Box style={{ display: 'flex', flexDirection: 'row', width: '50%', gap: 16 }}>
                    <SmartCard flex={1}>
                        <ChartCard
                            title='TCOC by LOB'
                            chart={(
                                <DonutChart
                                    isLoading={isLoading}
                                    data={{
                                        datasets: [
                                            { data: tcocLOB?.map(x => x.value) ?? [] }
                                        ],
                                        labels: tcocLOB?.map(x => x.name) ?? []
                                    }}
                                    options={{ ...DonutChartDefaultOptions, plugins: { ...DonutChartDefaultOptions.plugins, legend: { position: 'bottom' } } }}
                                />
                            )}
                        />
                    </SmartCard>
                    <SmartCard flex={1}>
                        <ChartCard
                            title='TCOC by Contract Type'
                            chart={(
                                <DonutChart
                                    isLoading={isLoading}
                                    data={{
                                        datasets: [
                                            { data: contractTypesTCOC?.map(x => x.value) ?? [] }
                                        ],
                                        labels: contractTypesTCOC?.map(x => x.name) ?? []
                                    }}
                                    options={{ ...DonutChartDefaultOptions, plugins: { ...DonutChartDefaultOptions.plugins, legend: { position: 'bottom' } } }}
                                />
                            )}
                        />
                    </SmartCard>
                </Box>
            </Tile.Row>
            <Tile.Row flex={0} spacing={2} style={{ minHeight: 380 }}>
                <SmartCard style={{ width: '50%' }}>
                    <ChartCard
                        title='Provider Performace'
                        options={[
                            { label: 'worst', onClick: () => setProviderPerformanceOrder('worst'), isActive: providerPerformanceOrder === 'worst' },
                            { label: 'best', onClick: () => setProviderPerformanceOrder('best'), isActive: providerPerformanceOrder === 'best' }
                        ]}
                        chart={(
                            <HorizontalBarChart
                                isLoading={isLoading}
                                data={{
                                    datasets: [
                                        { data: orderProvidersTCOC.map(x => x.value), backgroundColor: orderProvidersTCOC.map(x => x.value < 10000 ? theme.palette.error[500] : x.value < 50000 ? theme.palette.primary[500] : theme.palette.success[400]) }
                                    ],
                                    labels: orderProvidersTCOC.map(x => x.name)
                                }}
                            />
                        )}
                    />
                </SmartCard>
                <SmartCard style={{ width: '50%' }}>
                    <ChartCard
                        title='Contract Performace'
                        options={[
                            { label: 'below', onClick: () => setQMPerformanceOrder('below'), isActive: qmPerformanceOrder === 'below' },
                            { label: 'meets', onClick: () => setQMPerformanceOrder('meets'), isActive: qmPerformanceOrder === 'meets' }
                        ]}
                        chart={(
                            <HorizontalBarChart
                                isLoading={isLoading}
                                data={{
                                    datasets: [
                                        { data: orderContractsTCOC.map(x => x.value), backgroundColor: orderContractsTCOC.map(x => x.value < 60000 ? theme.palette.error[500] : x.value < 80000 ? theme.palette.primary[500] : theme.palette.success[400]) }
                                    ],
                                    labels: orderContractsTCOC.map(x => x.name)
                                }}
                            />
                        )}
                    />
                </SmartCard>
            </Tile.Row>
            <StaleObserver status={status} />
        </Tile.Container>
    )
}
